import type { UseMutationResult } from '@tanstack/react-query';
import { SaveIcon } from 'lucide-react';
import { type Dispatch, type KeyboardEvent, type SetStateAction, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useClickOutSide } from '@/hooks/useClickOutside';
import type { Bookmark } from '@/types/bookmarks';

export const EditInput = ({
  bookmark,
  updateBookmark,
  setEditId,
}: {
  bookmark: Bookmark;
  updateBookmark: UseMutationResult<Bookmark, Error, Bookmark, unknown>;
  setEditId: Dispatch<SetStateAction<number | null>>;
}) => {
  const ref = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<Bookmark>(bookmark);

  useClickOutSide(ref, () => {
    // Without timeout clicking out side in edit mode will trigger page redirect
    setTimeout(() => {
      setEditId((prev) => (prev === bookmark.id ? null : prev));
    }, 200);
  });

  type OnKeyEnterHandler = (event: KeyboardEvent<HTMLInputElement>) => void;
  const onKeyEnter: OnKeyEnterHandler = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      updateBookmark.mutate(data);
    }
  };
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div
      ref={ref}
      className='flex items-center gap-2'
    >
      <Input
        name='edit-bookmark'
        ref={inputRef}
        className='mt-0'
        loading={updateBookmark.isPending ? 'show' : 'hidden'}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyEnter}
        value={data.name}
        onChange={(e) => {
          e.stopPropagation();
          setData({ ...bookmark, name: e.target.value });
        }}
      />
      <Button
        variant='outline'
        onClick={(e) => {
          e.stopPropagation();
          updateBookmark.mutate(data);
          setEditId(null);
        }}
      >
        <SaveIcon
          size={10}
          strokeWidth={2}
          className='h-4 w-4'
        />
      </Button>
    </div>
  );
};
